export type InstallAction =
  { outcome: 'accepted' | 'dismissed'; type: 'prompt-result' } | { type: 'app-installed' } | { type: 'prompt-captured' }

export interface InstallState {
  status: InstallStatus
}

/**
 * Where the PWA is in its install lifecycle, derived purely from the browser's install
 * signals (`beforeinstallprompt`, the prompt's user choice, and `appinstalled`).
 *
 * - `unavailable` — the browser has not offered installability (or the platform, e.g. iOS
 *   Safari, never fires `beforeinstallprompt`; the UI shows the manual guide instead).
 * - `available` — a deferred prompt is held and can be shown on a user gesture.
 * - `dismissed` — the user declined the native prompt; the UI falls back to a manual hint.
 * - `installed` — terminal; the app is installed to the home screen.
 */
export type InstallStatus = 'available' | 'dismissed' | 'installed' | 'unavailable'

export const initialInstallState: InstallState = { status: 'unavailable' }

/**
 * Pure transition for the install lifecycle. Kept free of any DOM/event handles so the whole
 * state machine is unit-testable; the React hook wires real browser events onto these actions.
 * `installed` is terminal — once the app is on the home screen, later prompt signals are noise.
 */
export const installReducer = (state: InstallState, action: InstallAction): InstallState => {
  if (state.status === 'installed') {
    return state
  }

  switch (action.type) {
    case 'app-installed':
      return { status: 'installed' }
    case 'prompt-captured':
      return { status: 'available' }
    case 'prompt-result':
      return { status: action.outcome === 'accepted' ? 'installed' : 'dismissed' }
  }
}
